import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  adminService,
  type DonationButtonAction,
  type DonationOption,
} from "@/project/jain_elibrary-admin/services/adminService";
import { Loader2, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const BUTTON_ACTIONS: { value: DonationButtonAction; label: string }[] = [
  { value: "none", label: "No button" },
  { value: "paypal", label: "PayPal donation" },
  { value: "donate_form", label: "India donation form" },
  { value: "fill_form", label: "Zelle receipt form" },
  { value: "url", label: "External link" },
];

const quillModules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

type FormState = Omit<DonationOption, "id" | "created_on" | "updated_on">;

const emptyOption = (): FormState => ({
  title: "",
  details_html: "",
  button_action: "none",
  button_label: "",
  button_url: "",
  icon_style: "brown",
  sort_order: 0,
  is_active: true,
});

export default function DonationOptions() {
  const [options, setOptions] = useState<DonationOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<DonationOption | null>(null);
  const [form, setForm] = useState<FormState>(emptyOption());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setOptions(await adminService.getDonationOptions());
    } catch (e: unknown) {
      console.error(e);
      toast.error("Could not load donation options.");
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    const nextOrder =
      options.reduce((max, o) => Math.max(max, o.sort_order), 0) + 1;
    setForm({ ...emptyOption(), sort_order: nextOrder });
    setDialogOpen(true);
  };

  const openEdit = (o: DonationOption) => {
    setEditing(o);
    setForm({
      title: o.title,
      details_html: o.details_html ?? "",
      button_action: o.button_action,
      button_label: o.button_label ?? "",
      button_url: o.button_url ?? "",
      icon_style: o.icon_style,
      sort_order: o.sort_order,
      is_active: o.is_active,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    const payload: Partial<DonationOption> = {
      title: form.title.trim(),
      details_html: form.details_html,
      button_action: form.button_action,
      button_label: form.button_label.trim(),
      button_url: form.button_url.trim(),
      icon_style: form.icon_style,
      sort_order: form.sort_order,
      is_active: form.is_active,
    };
    try {
      setSaving(true);
      if (editing) {
        await adminService.updateDonationOption(editing.id, payload);
        toast.success("Donation option updated.");
      } else {
        await adminService.createDonationOption(payload);
        toast.success("Donation option added.");
      }
      setDialogOpen(false);
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      toast.error(err?.response?.data?.detail ?? "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (o: DonationOption) => {
    if (!window.confirm(`Delete "${o.title}"? This cannot be undone.`)) {
      return;
    }
    try {
      await adminService.deleteDonationOption(o.id);
      toast.success("Deleted.");
      await load();
    } catch (e: unknown) {
      console.error(e);
      toast.error("Delete failed.");
    }
  };

  const actionLabel = (a: DonationButtonAction) =>
    BUTTON_ACTIONS.find(x => x.value === a)?.label ?? a;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex min-h-[40vh] items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
          Loading…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mx-auto max-w-5xl space-y-6 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="mb-2 text-xl font-semibold tracking-tight md:text-3xl">
              Donation Options
            </h1>
            <p className="text-sm text-muted-foreground">
              The cards shown on the public Donate page. Add, edit, reorder (via
              Sort order) or hide any card without code changes.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void load()}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button type="button" size="sm" onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Add option
            </Button>
          </div>
        </div>

        {options.length === 0 ? (
          <p className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
            No donation options yet. Click “Add option” to create one.
          </p>
        ) : (
          <ul className="space-y-2">
            {options.map(o => (
              <li
                key={o.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-4 shadow-sm"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                      #{o.sort_order}
                    </span>
                    <p className="truncate font-medium">{o.title}</p>
                    {!o.is_active && (
                      <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase text-orange-800">
                        Hidden
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Button: {actionLabel(o.button_action)}
                    {o.button_action !== "none" && o.button_label
                      ? ` (“${o.button_label}”)`
                      : ""}{" "}
                    · Icon: {o.icon_style}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(o)}
                  >
                    <Pencil className="mr-1 h-3.5 w-3.5" />
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => void remove(o)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit donation option" : "New donation option"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid max-h-[70vh] gap-4 overflow-y-auto py-2 pr-1">
            <div className="grid gap-2">
              <Label htmlFor="do-title" required>
                Title
              </Label>
              <Input
                id="do-title"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. US Dollar via Bank Draft – Tax deductible in USA"
              />
            </div>

            <div className="grid gap-2">
              <Label>Details (shown under the title)</Label>
              <div className="rounded-md border bg-card">
                <ReactQuill
                  theme="snow"
                  value={form.details_html}
                  onChange={v => setForm(f => ({ ...f, details_html: v }))}
                  modules={quillModules}
                  className="[&_.ql-editor]:min-h-[140px]"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label>Button action</Label>
                <Select
                  value={form.button_action}
                  onValueChange={v =>
                    setForm(f => ({
                      ...f,
                      button_action: v as DonationButtonAction,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUTTON_ACTIONS.map(a => (
                      <SelectItem key={a.value} value={a.value}>
                        {a.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Icon style</Label>
                <Select
                  value={form.icon_style}
                  onValueChange={v =>
                    setForm(f => ({
                      ...f,
                      icon_style: v === "dark" ? "dark" : "brown",
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brown">Brown</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.button_action !== "none" && (
              <div className="grid gap-2">
                <Label htmlFor="do-btn-label">Button label</Label>
                <Input
                  id="do-btn-label"
                  value={form.button_label}
                  onChange={e =>
                    setForm(f => ({ ...f, button_label: e.target.value }))
                  }
                  placeholder="e.g. Donate Now"
                />
              </div>
            )}

            {form.button_action === "url" && (
              <div className="grid gap-2">
                <Label htmlFor="do-btn-url">Button link (URL)</Label>
                <Input
                  id="do-btn-url"
                  value={form.button_url}
                  onChange={e =>
                    setForm(f => ({ ...f, button_url: e.target.value }))
                  }
                  placeholder="https://…"
                />
              </div>
            )}

            <div className="grid grid-cols-2 items-end gap-3">
              <div className="grid gap-2">
                <Label htmlFor="do-order">Sort order</Label>
                <Input
                  id="do-order"
                  type="number"
                  min={0}
                  value={form.sort_order}
                  onChange={e =>
                    setForm(f => ({
                      ...f,
                      sort_order: Number(e.target.value) || 0,
                    }))
                  }
                />
              </div>
              <div className="flex items-center gap-2 pb-2">
                <Switch
                  id="do-active"
                  checked={form.is_active}
                  onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))}
                />
                <Label htmlFor="do-active">Active (visible on site)</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" disabled={saving} onClick={() => void save()}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
